import React from "react";
import {
  Home,
  LayoutDashboard,
  Settings,
  LifeBuoy,
  LogOut,
  Cloud,
  Bot,
  Store,
  History,
  PenLine, 
  ShieldAlert,
  Wrench,
  PlugZap,
  Milestone,
  ChevronsUpDown,
  BookOpen
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
} from "@/components/ui/sidebar";
import { useTranslation } from "@/lib/i18n";
import {
  useAuthLogout,
  useUserRole,
  useActiveWorkspace,
  useUserWorkspaces,
  useSwitchWorkspace
} from "@/lib/auth-store";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
export function AppSidebar(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const logout = useAuthLogout();
  const userRole = useUserRole();
  const activeWorkspace = useActiveWorkspace();
  const workspaces = useUserWorkspaces();
  const switchWorkspace = useSwitchWorkspace();
  const handleLogout = () => {
    logout();
    toast.info("Logged out successfully");
    navigate("/login");
  };
  const isActive = (path: string) => 
    location.pathname.startsWith(path) && (path !== "/" || location.pathname === "/");
  const menuItems = [
    { path: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/agents", icon: Bot, label: "Agents" },
    { path: "/marketplace", icon: Store, label: "Marketplace" },
    { path: "/prompts", icon: PenLine, label: "Prompts" },
    { path: "/tools", icon: Wrench, label: "Tools" },
    { path: "/integrations", icon: PlugZap, label: "Integrations" },
    { path: "/audit", icon: History, label: "Logs", restricted: userRole === 'Viewer' },
    { path: "/enterprise/roadmap", icon: Milestone, label: "Roadmap" },
  ];
  return (
    <Sidebar className="border-r border-sidebar-border/80 bg-sidebar/95">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-3 py-4">
          <div className="h-9 w-9 rounded-2xl bg-gradient-to-br from-sidebar-primary via-chart-4 to-chart-2 flex items-center justify-center shadow-glow">
            <Cloud className="h-5 w-5 text-sidebar-primary-foreground" />
          </div>
          <span className="text-lg font-display font-bold tracking-tight text-sidebar-foreground">Cloudface</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => navigate("/")} isActive={isActive("/")}>
                <Home className="size-4" /> <span>Landing</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            {menuItems.filter(i => !i.restricted).map(item => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton onClick={() => navigate(item.path)} isActive={isActive(item.path)}>
                  <item.icon className="size-4" /> <span>{item.label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
        <SidebarGroup className="mt-auto">
          <SidebarMenu>
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton isActive={isActive("/docs")}>
                    <BookOpen className="size-4" /> <span>{t('docs_title')}</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-48">
                  <DropdownMenuItem onClick={() => navigate("/docs")}>Handover Guide</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/docs/agents")}>Agents Spec</DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate("/docs/hardening")} className="text-primary font-bold"><ShieldAlert className="w-3.5 h-3.5 mr-2"/> Hardening Report</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => navigate("/settings")} isActive={isActive("/settings")}>
                <Settings className="size-4" /> <span>Settings</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
            <SidebarMenuItem>
              <SidebarMenuButton onClick={() => navigate("/help")} isActive={isActive("/help")}>
                <LifeBuoy className="size-4" /> <span>{t('help_title')}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border/80">
        <SidebarMenu className="p-2">
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton className="h-12 w-full justify-between rounded-2xl border border-sidebar-border/80 bg-sidebar-accent/80 hover:bg-sidebar-accent">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-xl bg-sidebar-primary/20 text-sidebar-primary flex items-center justify-center text-[10px] font-bold">
                      {activeWorkspace.name.substring(0, 1)}
                    </div>
                    <div className="flex flex-col items-start overflow-hidden">
                      <span className="text-xs font-bold truncate w-24 text-left text-sidebar-foreground">{activeWorkspace.name}</span>
                      <span className="text-[10px] text-sidebar-foreground/60">{activeWorkspace.type}</span>
                    </div>
                  </div>
                  <ChevronsUpDown className="w-3 h-3 text-sidebar-foreground/60" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 mb-2">
                {workspaces.map(ws => (
                  <DropdownMenuItem
                    key={ws.id}
                    onClick={() => switchWorkspace(ws.id)}
                    className={ws.id === activeWorkspace.id ? "bg-accent" : ""}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium text-xs">{ws.name}</span>
                      <span className="text-[10px] text-muted-foreground">{ws.type}</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="text-destructive hover:text-destructive">
              <LogOut className="size-4" /> <span>{t('logout')}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
